import { useEffect, useId, useState } from "react";
import type { FormEvent } from "react";
import { PageHeader } from "@/components/PageHeader";
import { StateMessage } from "@/components/StateMessage";
import { Why } from "@/components/Why";
import { demoApi, problemMessage } from "@/api/client";
import type { BusinessEntity, CropPlantingReport } from "@/api/types";
import "./ReportsCropPlanting.css";

type ReportStatus = "idle" | "loading" | "ready" | "error";

/** The current UTC year — a sensible default so the Year field is never an empty
 *  input, same "seed a sensible default, never fetch until Show" posture
 *  `ReportsTrialBalance.tsx`'s own `defaultAsOf` gives for its own date field. */
function defaultYear(): number {
  return new Date().getUTCFullYear();
}

/**
 * Crop planting summary — Reports' worksheet of what was planted where, and how many
 * acres, for one entity and one caller-chosen year. ef-055-2, ADR 0009 §6 ("Reports is
 * one picker" — a report renders inside the template at `/reports/{slug}`, no area tab
 * strip of its own; "All reports" below is the only way back).
 *
 * <p>
 * ENTITY IS REQUIRED, SO THIS SCREEN CARRIES ITS OWN PICKER — unlike
 * `ReportsCropProduction.tsx`'s own no-entity-picker default,
 * `CropPlantingReportEndpoints` has no "resolve the tenant's own single/oldest entity"
 * fallback (`ef-055-2-build.md`'s own open question, kept required). Same shape
 * `ReportsPartnerSplitsDollar.tsx`'s own "Entity" picker already uses: seeded once from
 * the server's own entity list, a "Show" button fetches on click.
 * </p>
 *
 * <p>
 * NOTHING IS FETCHED UNTIL Show IS PRESSED — same "no guessed window" discipline every
 * other report screen's own remarks give: a planting summary read for an entity/year
 * nobody asked about is a number a farmer did not request. The Year input still carries
 * the current year as a sensible default so the form is never an empty field.
 * </p>
 *
 * <p>
 * ONE ROW PER (FIELD, CROP), NEVER SUMMED — a field that double-crops (two crops, same
 * field and year) renders as two distinct rows, same grouping
 * `CropPlantingReportRepository`'s own remarks describe.
 * </p>
 */
export function ReportsCropPlanting() {
  const entityFieldId = useId();
  const yearFieldId = useId();

  const [entities, setEntities] = useState<BusinessEntity[] | null>(null);
  const [entityId, setEntityId] = useState<string>("");
  const [year, setYear] = useState<string>(String(defaultYear()));

  const [report, setReport] = useState<CropPlantingReport | null>(null);
  const [status, setStatus] = useState<ReportStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    demoApi
      .getEntities(controller.signal)
      .then((rows) => {
        setEntities(rows);
        // Seeded once, from the server's own list — never left blank when there is
        // exactly one entity to pick, the common single-entity farm this booth demo
        // itself models (db/seed/0002_seed_demo.sql).
        setEntityId((current) => current || (rows[0]?.id ?? ""));
      })
      .catch(() => setEntities([]));
    return () => controller.abort();
  }, []);

  const yearAsNumber = year === "" ? null : Number(year);
  const canSubmit = entityId !== "" && yearAsNumber !== null && !Number.isNaN(yearAsNumber) && status !== "loading";

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!canSubmit || yearAsNumber === null) return;

    setStatus("loading");
    setError(null);
    try {
      const result = await demoApi.getCropPlantingReport({ entityId, year: yearAsNumber });
      setReport(result);
      setStatus("ready");
    } catch (err) {
      setReport(null);
      setStatus("error");
      setError(problemMessage(err, "Could not load the crop planting summary."));
    }
  }

  /**
   * The export is its own request, fired on the click — not fetched alongside the
   * report, same reasoning every other report screen's own identical download gives:
   * paying for the whole CSV body on every entity/year change would be wasted work for
   * the visits that never press the button.
   */
  const [csvState, setCsvState] = useState<
    { kind: "idle" } | { kind: "busy" } | { kind: "done"; rows: number } | { kind: "error"; message: string }
  >({ kind: "idle" });

  async function downloadCsv(): Promise<void> {
    if (!canSubmit || yearAsNumber === null) return;
    setCsvState({ kind: "busy" });
    try {
      const csv = await demoApi.getCropPlantingCsv({ entityId, year: yearAsNumber });

      // The bytes are the server writer's output verbatim — same "add a blob and a
      // filename and nothing else" discipline every other report screen's own identical
      // download follows.
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `easyfarm-crop-planting-${yearAsNumber}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      const rows = Math.max(csv.trim().split("\n").length - 1, 0);
      setCsvState({ kind: "done", rows });
    } catch (err) {
      setCsvState({
        kind: "error",
        message: problemMessage(err, "Could not build the crop planting file."),
      });
    }
  }

  function handleAllReportsClick(): void {
    // Self-contained, same device every other report screen's own handler already uses
    // rather than threading a `navigate` prop through every screen this job touches:
    // pushState doesn't fire popstate on its own, so App.tsx's existing listener is told
    // directly.
    window.history.pushState({}, "", "/reports");
    window.dispatchEvent(new PopStateEvent("popstate"));
  }

  const rows = report?.rows ?? [];
  const isEmpty = status === "ready" && rows.length === 0;

  return (
    <main className="reports-crop-planting">
      <PageHeader
        title="Crop planting summary"
        subtitle="What was planted where, and how many acres, for one year"
      />

      <a
        className="reports-crop-planting__all-reports"
        href="/reports"
        onClick={(event) => {
          event.preventDefault();
          handleAllReportsClick();
        }}
      >
        ← All reports
      </a>

      <section
        className="reports-crop-planting__panel"
        aria-labelledby="reports-crop-planting-heading"
        data-state={isEmpty ? "empty" : status}
      >
        <h2 id="reports-crop-planting-heading" className="reports-crop-planting__heading">
          Summary
        </h2>

        <form className="reports-crop-planting__form" onSubmit={(event) => void handleSubmit(event)}>
          <div className="reports-crop-planting__field">
            <label htmlFor={entityFieldId}>Entity</label>
            <select
              id={entityFieldId}
              value={entityId}
              onChange={(event) => setEntityId(event.target.value)}
              disabled={entities === null || entities.length === 0}
            >
              {entities === null ? (
                <option value="">Loading…</option>
              ) : entities.length === 0 ? (
                <option value="">No entities on this farm yet</option>
              ) : (
                entities.map((entity) => (
                  <option key={entity.id} value={entity.id}>
                    {entity.name}
                  </option>
                ))
              )}
            </select>
          </div>

          <div className="reports-crop-planting__field">
            <label htmlFor={yearFieldId}>Year</label>
            <input
              id={yearFieldId}
              type="number"
              inputMode="numeric"
              value={year}
              onChange={(event) => setYear(event.target.value)}
            />
          </div>

          <button
            type="submit"
            className="btn btn--primary reports-crop-planting__show"
            disabled={!canSubmit}
          >
            {status === "loading" ? "Loading…" : "Show"}
          </button>
        </form>

        <Why>
          Rows are grouped by <strong>field</strong>, then <strong>crop</strong> — a
          double-cropped field renders as two rows, one per crop.
        </Why>

        {status === "error" && (
          <StateMessage
            tone="error"
            title="Could not load the crop planting summary."
            detail={error ?? undefined}
          />
        )}

        {isEmpty && (
          <StateMessage
            tone="empty"
            title="Nothing to show for this year."
            detail="No planting activity was found for this entity and year."
          />
        )}

        {status === "ready" && rows.length > 0 && (
          <>
            <div
              className="reports-crop-planting__table-wrap"
              tabIndex={0}
              role="group"
              aria-labelledby="reports-crop-planting-heading"
            >
              <table className="reports-crop-planting__table ruled-rows">
                <caption className="reports-crop-planting__caption">
                  {rows.length} {rows.length === 1 ? "line" : "lines"}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Field</th>
                    <th scope="col">Crop</th>
                    <th scope="col" className="reports-crop-planting__num">Acres</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.fieldCropId}>
                      <th scope="row" className="reports-crop-planting__field-name">{row.fieldName}</th>
                      <td>{row.crop}</td>
                      <td className="reports-crop-planting__num">{row.acres.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="reports-crop-planting__export">
              <h3 className="reports-crop-planting__export-title">Crop planting summary for your records</h3>
              <p className="reports-crop-planting__export-copy">
                The same rows above, as a CSV a spreadsheet can open.
              </p>
              <button
                type="button"
                className="btn btn--secondary reports-crop-planting__export-btn"
                disabled={csvState.kind === "busy" || !canSubmit}
                onClick={() => void downloadCsv()}
              >
                {csvState.kind === "busy" ? "Building the file…" : "Download CSV"}
              </button>

              {csvState.kind === "done" && (
                <p className="reports-crop-planting__export-result" role="status">
                  {csvState.rows} {csvState.rows === 1 ? "line" : "lines"} saved.
                </p>
              )}

              {csvState.kind === "error" && (
                <p className="reports-crop-planting__export-error" role="alert">
                  {csvState.message} Nothing was downloaded.
                </p>
              )}
            </div>
          </>
        )}
      </section>
    </main>
  );
}
